import { defineStore, getActivePinia } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { useLocalStorage, useStorage } from '@vueuse/core'
import { useUsuariosStore, useGrupoDePermissoes } from '@/stores'
import { getInitials, toast } from '@/common/util'
import { useThemeSync } from '@/composables/useThemeSync'
import { useJourneys } from '@/composables/useJourneys'

import webStorage from '@/common/webStorage'
import oidcService from '@/services/aliare/oidc.service'
import autenticacaoService from '@/services/autenticacao.service'

import type { Pinia, Store } from 'pinia'
import type { LoaderType, UsuarioType } from '@/types/types'

interface ExtendedPinia extends Pinia {
  _s: Map<string, Store>
}

/**
 * Identidade do usuário autenticado.
 *
 * Estes campos vinham nas claims do access token e eram lidos decodificando o próprio JWT. O
 * Aliare Auth deixou de emiti-los no token, mantendo apenas os campos de protocolo (nbf, exp,
 * iss, aud, client_id, jti, iat, scope, amr), e passou a expô-los no /connect/userinfo.
 *
 * Os nomes são mantidos exatamente como as claims originais para que os consumidores do
 * `payload` continuem funcionando sem alteração.
 */
export interface identityType {
  sub?: string
  IdDoUsuario?: string
  unique_name?: string
  email?: string
  Tenant?: string
  Level?: string
}

export interface tokenType {
  access_token: string
  expires_in: number
  refresh_token: string
  scope: string
  token_type: string
  method?: 'sso' | 'local',
  updatePassword?: boolean
  /** Identidade devolvida pela API de autenticação junto com o token. */
  user?: identityType | null
  /** Presente quando o tenant exige segundo fator; nesse caso o token vem nulo. */
  twoFactor?: twoFactorChallengeType | null
}

/**
 * Desafio de segundo fator devolvido pelo login.
 *
 * O `ticket` é a referência ao token que ficou retido no servidor — ele não é credencial: usado
 * como Bearer, a API responde 401.
 */
export interface twoFactorChallengeType {
  ticket: string
  /** Canal do envio. Hoje sempre `email`, mas a tela deve ler daqui em vez de assumir. */
  channel: string
  /** Destino mascarado, para a pessoa saber onde procurar o código. */
  destinationHint: string
  /** Validade do código atual, em segundos a partir de agora. */
  expiresInSeconds: number
  /** Quanto falta para poder pedir outro código, em segundos a partir de agora. */
  resendInSeconds: number
}

const initialState: tokenType = {
  access_token: '',
  expires_in: 0,
  refresh_token: '',
  scope: '',
  token_type: '',
  method: undefined,
  updatePassword: false
}

const initialStateClientMode = {
  tenant: '',
  contract: ''
}

const initialStatePerson: UsuarioType = {
  nomeCompleto: '',
  email: '',
  cpf: '',
  telefone: '',
  dataDeAdmissao: '',
  superiorDireto: '',
  cargoId: '',
  departamentoId: '',
  unidadeDeNegocioId: '',
  ativo: false,
  tipoPessoa: 'Fisica',
  dataTermoDeUsoAceito: null,
  termoDeUsoId: null
}

export const useAuthStore = defineStore('auth', () => {
  const usuarioStore = useUsuariosStore()
  const grupoDePermissoes = useGrupoDePermissoes()

  const subscription = useLocalStorage<any>('sub', {
    id: '',
    status: '',
    createdAt: null as Date | null,
    expiresAt: null as Date | null,
    canceledAt: null as Date | null
  }, {
    serializer: {
      read: (value: string) => JSON.parse(window.atob(value)),
      write: (value: any) => window.btoa(JSON.stringify(value))
    }
  })

  const nStatus = reactive({
    authenticate: 'initial' as LoaderType,
    getUserInfo: 'initial' as LoaderType,
    getUserAccounts: 'initial' as LoaderType,
    verifyTwoFactor: 'initial' as LoaderType,
    resendTwoFactor: 'initial' as LoaderType
  })

  /**
   * @EXPERIMENTAL: Persiste dados simples do usuário na useLocalStorage
   * Se o ID não esta preenchido significa que as informações do usuário não foram carregadas
   * useLocalStorage é reativo e compartilhado entre as abas do navegador
   */
  const PIInitialValue = {
    id: '',
    name: '',
    fullName: '',
    role: '',
    avatar: '',
    birthdate: null as Date | null,
    lastUpdated: null as Date | null,
    supervisor: {
      id: '',
      name: ''
    },
    unidade: {
      id: '',
      name: ''
    }
  }
  const pInfo = useLocalStorage<any>('userInfo', PIInitialValue, { mergeDefaults: true })
  const resetPI = () => Object.assign(pInfo.value, {
    ...PIInitialValue
  })

  const isDistribuidor = useStorage('isDistribuidor', false, localStorage, { mergeDefaults: true })

  /**
   * States
   */
  const token = reactive<tokenType>({
    ...initialState,
    ...webStorage.getItem('token')
  })

  const needsPasswordUpdate = ref(false)

  /**
   * Desafio de segundo fator em andamento.
   *
   * Só em memória, deliberadamente: o ticket destrava um token já emitido, e um refresh custar um
   * login novo é preço menor do que deixar essa referência no storage do navegador. Some sozinho
   * ao recarregar a página, e o guard da rota devolve a pessoa para o login.
   */
  const twoFactorChallenge = ref<twoFactorChallengeType | null>(null)

  /** O "lembrar-me" escolhido no login, guardado para valer quando a validação concluir. */
  const twoFactorPersist = ref(false)

  const clearTwoFactorChallenge = () => {
    twoFactorChallenge.value = null
    twoFactorPersist.value = false
  }

  /**
   * @description: Atualiza o token no state e no localStorage
   * @param data tokenType | null
   * @param local boolean
   */
  const setToken = (data: tokenType | null, local?: boolean) => {
    Object.assign(token, data === null ? initialState : data)
    webStorage.setItem('token', data === null ? initialState : data, local ?? true)
  }

  /**
   * @description: Identidade do usuário, obtida do /connect/userinfo — pela nossa API no fluxo
   * local, ou pelo próprio oidc-client no fluxo SSO. Persistida com o mesmo webStorage e o mesmo
   * flag `local` do token, para que as duas coisas tenham exatamente o mesmo tempo de vida.
   */
  const identity = ref<identityType>(webStorage.getItem('identity') || {})

  const setIdentity = (data: identityType | null, local?: boolean) => {
    identity.value = data || {}

    if (data === null) webStorage.removeItem('identity')
    else webStorage.setItem('identity', data, local ?? true)
  }

  const partnerIdPlantar = ref((window as any).configuration.plantar.partnerId)
  const tenantCurrentPartner = ref(webStorage.getItem('tenant-partner'))

  const clientMode = reactive<typeof initialStateClientMode>({
    ...initialStateClientMode,
    ...webStorage.getItem('client-mode')
  })

  const userData = reactive<UsuarioType>({ ...initialStatePerson })

  /**
   * Getters
   */
  const mustAcceptTermsOfUse = computed(() => (!userData.dataTermoDeUsoAceito && !!userData.termoDeUsoId))

  const userId = computed<string>(() => userData?.id || pInfo.value?.id || '')
  const userName = computed<string>(() => userData?.apelido || pInfo.value?.name || '')

  const userInitials = computed(() => getInitials(userData.nomeCompleto) || '')

  /**
   * @description: Dados de identidade do usuário (sub, IdDoUsuario, unique_name, email, Tenant,
   * Level). A origem é o /connect/userinfo, resolvido no login/refresh.
   *
   * O fallback decodifica o access token, cobrindo a janela de transição em que o Aliare Auth ainda
   * emite as claims dentro do token — é o que permite subir esta mudança antes de o Auth mudar o
   * formato. O try/catch é necessário porque `window.atob` não aceita os caracteres `-` e `_` do
   * base64url: sem ele, um token com esses caracteres no payload derruba o store inteiro.
   */
  const payload = computed<identityType>(() => {
    if (Object.keys(identity.value).length) return identity.value

    try {
      return token.access_token ? JSON.parse(window.atob(token.access_token.split('.')[1])) : {}
    } catch {
      return {}
    }
  })
  const loggedIn = computed(() => !!token.access_token)
  const accessToken = computed(() => token.access_token)
  const hasElevatedPrivileges = computed(() => (['N0', 'N1'].includes(payload.value?.Level) && !clientMode.tenant))

  // Modo exclusivo para usuário plantar que não esteja se passando por admin de um cliente
  const inAdminMode = computed(() => hasElevatedPrivileges.value || isDistribuidor.value)
  const getTenantCurrentPartner = computed(() => tenantCurrentPartner.value)
  const getActiveTentant = computed<string>(() => clientMode.tenant || tenantCurrentPartner.value || payload.value?.Tenant || '')

  const getUserAccounts = () => {
    nStatus.getUserAccounts = 'loading'

    const pInfo = useLocalStorage<any>('userInfo', { multiplePartners: false }, { mergeDefaults: true })
    pInfo.value.multiplePartners = false

    return usuarioStore
      .getUserAccounts()
      .then(accounts => {
        nStatus.getUserAccounts = 'success'
        if (accounts?.length && accounts.length === 1) setTenantIdPartner(accounts[0].tenantId)
      })
      .catch(() => {
        nStatus.getUserAccounts = 'error'
      })
  }

  /**
   * Actions
   */
  /**
   * Passo final do login, comum aos dois caminhos: com segundo fator e sem. O `Verify` devolve
   * exatamente o mesmo formato do login, então o tratamento tem que ser o mesmo — se divergir, um
   * dos dois caminhos vai montar a sessão pela metade.
   */
  const completeLogin = (result: tokenType, persist?: boolean) => {
    if (!result?.access_token) return

    if (persist) localStorage.setItem('remember', String(persist))
    setToken({ ...result, method: 'local' }, persist)

    // Sempre sobrescreve, inclusive com null: um login novo não pode herdar a identidade do
    // usuário anterior. Com null, o `payload` cai no fallback de decodificar o token.
    setIdentity(result.user || null, persist)

    needsPasswordUpdate.value = result.updatePassword || false
  }

  /**
   * Troca o código digitado pela sessão. Só a partir daqui o fluxo segue igual ao de um login sem
   * segundo fator — inclusive o `getUserAccounts`, que é o gatilho do roteamento pós-login.
   */
  const verifyTwoFactor = async (code: string): Promise<tokenType> => {
    if (!twoFactorChallenge.value) throw new Error('Sem desafio de segundo fator em andamento.')

    nStatus.verifyTwoFactor = 'loading'

    try {
      const result = await autenticacaoService.verifyTwoFactor({
        ticket: twoFactorChallenge.value.ticket,
        code
      }) as tokenType

      completeLogin(result, twoFactorPersist.value)
      clearTwoFactorChallenge()

      nStatus.verifyTwoFactor = 'success'
      getUserAccounts()

      return result
    } catch (e) {
      nStatus.verifyTwoFactor = 'error'

      throw e
    }
  }

  /**
   * Pede outro código. A API responde 200 mesmo quando ainda está no intervalo mínimo — nesse caso
   * nada é enviado e `resendInSeconds` traz o tempo que falta, que é o que a tela mostra no
   * contador. O ticket não muda: o desafio é o mesmo, só o código é trocado.
   */
  const resendTwoFactorCode = async (): Promise<twoFactorChallengeType | null> => {
    if (!twoFactorChallenge.value) return null

    nStatus.resendTwoFactor = 'loading'

    try {
      const challenge = await autenticacaoService.resendTwoFactor(twoFactorChallenge.value.ticket)

      twoFactorChallenge.value = challenge
      nStatus.resendTwoFactor = 'success'

      return challenge
    } catch (e) {
      nStatus.resendTwoFactor = 'error'

      throw e
    }
  }

  const authenticate = async (email: string, password: string, persist?: boolean, recaptchaToken?: string): Promise<tokenType> => {
    nStatus.authenticate = 'loading'

    try {
      const result = (await autenticacaoService.login({ username: email, password, recaptchaToken })) as tokenType

      // Tenant exige segundo fator: o token existe, mas ficou retido na API. Nada de sessão aqui —
      // nem token, nem identidade, nem a busca das contas, que iria sem credencial e tomaria 401.
      // Quem roteia para a tela de verificação é a chamadora, olhando este retorno.
      if (result?.twoFactor) {
        twoFactorChallenge.value = result.twoFactor
        twoFactorPersist.value = !!persist
        nStatus.authenticate = 'success'

        return result
      }

      clearTwoFactorChallenge()
      completeLogin(result, persist)

      nStatus.authenticate = 'success'
      getUserAccounts()

      return result
    } catch (e) {
      nStatus.authenticate = 'error'

      throw e
    }
  }

  const ssoAuthenticate = async (data: any) => {
    nStatus.authenticate = 'loading'

    const {
      access_token,
      expires_at: expires_in,
      token_type,
      refresh_token,
      scope,
      profile
    } = data

    setToken({
      access_token,
      expires_in,
      token_type,
      refresh_token,
      scope,
      method: 'sso'
    })

    // No SSO o token nunca passa pela nossa API, então a identidade não pode vir de lá. Mas o
    // oidc-client já resolve o /connect/userinfo por conta própria (loadUserInfo: true em
    // oidc.service.ts) e entrega o resultado mesclado em `profile` — basta aproveitá-lo.
    setIdentity(profile || null)

    nStatus.authenticate = 'success'
    await getUserAccounts()

    return true
  }

  const refresh = async () => {
    // @TODO: Acredito que não seja necessário, mas verificar se o refresh token é do SSO ou local
    // pra saber se precisa chamar o silentRefresh do oidcService ou do authService
    try {
      const result = (await autenticacaoService.silentRefresh(token.refresh_token)) as tokenType
      if (result?.access_token) {
        const isLocal = Boolean(localStorage.getItem('remember'))
        setToken({ ...result, method: 'local' }, isLocal)

        // Diferente do login, aqui só sobrescreve quando vem preenchido: é o mesmo usuário, então
        // uma falha momentânea do userinfo no refresh não deve descartar a identidade que já temos.
        if (result.user) setIdentity(result.user, isLocal)

        return true
      }
    } catch (error) {
      toast.error({
        title: 'Erro',
        message: 'Não foi possível atualizar sua sessão. Faça login novamente.',
        duration: 5000
      })

      logout()
    }

    return false
  }

  const setAvatar = (avatar: string) => {
    pInfo.value.avatar = avatar
  }

  const getUserInfo = async (id?: string): Promise<UsuarioType> => {
    nStatus.getUserInfo = 'loading'
    const userId = payload.value?.IdDoUsuario !== id && !!payload.value?.IdDoUsuario ? payload.value?.IdDoUsuario : id

    try {
      const data = await usuarioStore.getById(userId)
      Object.assign(userData, data)

      subscription.value = {}

      if (data?.usuarioAssinatura) {
        const { id, status, dataCriacao, dataExpiracao, dataCancelamento } = data.usuarioAssinatura
        Object.assign(subscription.value, {
          id,
          status,
          createdAt: dataCriacao ? new Date(dataCriacao) : null,
          expiresAt: dataExpiracao ? new Date(dataExpiracao) : null,
          canceledAt: dataCancelamento ? new Date(dataCancelamento) : null
        })
      }

      if (data.id) {
        resetPI()

        pInfo.value.id = data.id
        pInfo.value.name = data.apelido || data.nomeCompleto
        pInfo.value.fullName = data.nomeCompleto || ''
        pInfo.value.role = data.cargoNome || ''
        pInfo.value.avatar = data.foto || ''
        pInfo.value.supervisor = {
          id: data.superiorDireto || '',
          name: data.superiorDiretoNome || ''
        }
        pInfo.value.unidade = {
          id: data.unidadeDeNegocioId || '',
          name: data.unidadeDeNegocioNome || ''
        }
        pInfo.value.department = data.departamentoNome || ''

        if (data?.dataDeNascimento) pInfo.value.birthdate = new Date(data.dataDeNascimento)

        pInfo.value.lastUpdated = new Date()

        nStatus.getUserInfo = 'success'
      } else {
        nStatus.getUserInfo = 'error'
      }

      return data
    } catch {
      nStatus.getUserInfo = 'error'
    }
  }

  const logout = async () => {
    await useThemeSync().handleSessionEnd()
      .then(() => { })
      .catch(() => { })

    useJourneys().clear()
    Object.assign(userData, { ...initialStatePerson })
    resetPI()
    const isSSO = token.method === 'sso'

    usuarioStore.clearUserInfoNps()
    resetAllStates()
    setToken(null)
    setIdentity(null)
    exitClientMode()
    webStorage.removeItem('token')
    webStorage.removeItem('permissions')
    webStorage.removeItem('remember-me')
    webStorage.removeItem('showCommunication')
    webStorage.removeItem('modalIndicador')
    isDistribuidor.value = false

    window.sessionStorage.removeItem('filtersSessLearning')

    removeTenantPartner()

    try {
      const ssoMode = ((window as any)?.configuration?.oidc?.sso_mode || '').trim()
      if (!['', '__Oidc.SsoMode__'].includes(ssoMode) && isSSO) await oidcService.signoutRedirect()
    } catch (error) {
      //
      console.log('erro signout SSO', error)
    }
  }

  const setClientMode = ({ contract, tenant }: { contract: string; tenant: string }) => {
    resetAllStates()
    Object.assign(clientMode, { contract, tenant })
    webStorage.setItem('client-mode', { contract, tenant }, true)
  }

  const exitClientMode = () => {
    if (!clientMode.contract) return

    resetAllStates()
    setClientMode({ contract: '', tenant: '' })
    webStorage.removeItem('client-mode')
  }

  // Reseta todos os states do pinia (Ignorando AUTH)
  const resetAllStates = () => {
    const { _s: allStores } = getActivePinia() as ExtendedPinia
    allStores?.forEach((store) => store.$id !== 'auth' && store.$id !== 'system' && store.$reset())
    grupoDePermissoes.setPermissions()
  }

  const setTenantIdPartner = (tenantId: string) => {
    webStorage.setItem('tenant-partner', tenantId, true)
    tenantCurrentPartner.value = tenantId
  }

  const setUpdatePassword = (value: boolean) => {
    needsPasswordUpdate.value = value
  }

  const removeTenantPartner = () => {
    webStorage.removeItem('tenant-partner')
    tenantCurrentPartner.value = null
  }

  return {
    nStatus,
    persistentInfo: pInfo,
    token,
    partnerIdPlantar,
    userLogged: userData,
    userData,
    hasElevatedPrivileges,
    needsPasswordUpdate,
    twoFactorChallenge,
    clearTwoFactorChallenge,
    verifyTwoFactor,
    resendTwoFactorCode,
    // plantarMode,
    clientMode,
    // Getters
    payload,
    loggedIn,
    inAdminMode,
    getActiveTentant,
    accessToken,
    userId,
    userName,
    userInitials,
    mustAcceptTermsOfUse,
    getTenantCurrentPartner,
    subscription: computed(() => subscription.value),
    subscriptionExpired: computed(() => {
      if (!subscription.value.expiresAt) return false

      // Verifica se a data de expiração é menor que a data atual (ignorando horas, minutos e segundos)
      return new Date(subscription.value.expiresAt).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
    }),
    // Actions
    authenticate,
    ssoAuthenticate,
    refresh,
    logout,
    setClientMode,
    exitClientMode,
    getUserInfo,
    getUserAccounts,
    setTenantIdPartner,
    setUpdatePassword,
    resetUserTermsOfUse: () => {
      userData.dataTermoDeUsoAceito = null
    },
    setAvatar
  }
})

export default useAuthStore
